import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/utils/supabase';
import { useAuth } from '@/contexts/useAuth';
import { useEncompassFieldSync } from '@/hooks/useEncompassFieldSync';
import {
  addConcession,
  ConcessionDeleteError,
  deleteConcession,
  deleteConcessionGuarded,
  type LendwiseConcession
} from '@/services/lendwiseConcessionService';
import {
  computeWaterfallCapacity,
  allocateWaterfall,
  previewWaterfall,
  type BucketState,
  type WaterfallAllocation
} from '@/utils/concessionWaterfall';
import { toNumberOrNull } from '../utils/loanValueHelpers';
import { formatCurrency, formatNumber } from './format';
import type { ProspectLoan } from '../types';

/**
 * Concessions & Cure modal. Two tabs, live preview for the waterfall
 * allocation, direct service writes (concessions) and durable Encompass
 * queue writes (cure). Portals via shadcn Dialog; the inner container
 * carries `data-pricing-v2` so the modal CSS tokens resolve.
 */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loan: ProspectLoan;
  concessions: LendwiseConcession[];
  basePriceLocked: number;
  loanAmount: number;
  onLoanUpdate?: (updates: Partial<ProspectLoan>) => void;
  refreshConcessions: () => Promise<void>;
  /**
   * Committed Net LO Locked on the active lock with the current concessions
   * (par guard on) — saved pricing only, never the draft: a typed-but-unsaved
   * point must not authorise removing a concession the saved pricing still
   * needs. Removing a concession that would drop it below par is refused.
   * Null disables the guard (flag off).
   */
  netLoLocked?: number | null;
  /**
   * When set, every concession removal is refused with this reason — while a
   * candidate lock is being reviewed the committed basis is the active lock,
   * not the one on screen. Adding concessions stays allowed.
   */
  deleteBlockedReason?: string | null;
}

type Tab = 'concessions' | 'cure';

const BUCKET_LABELS: Record<BucketState['bucket'], string> = {
  level1: 'Level 1',
  branch_management: 'Branch Management',
  margin: 'Lendwise Margin'
};

const TOLERANCE_BY_SECTION: Record<string, 'zero' | 'ten' | 'unlimited'> = {
  A: 'zero',
  B: 'ten',
  C: 'unlimited'
};

const TOLERANCE_LABEL: Record<'zero' | 'ten' | 'unlimited', string> = {
  zero: '0% tolerance',
  ten: '10% tolerance',
  unlimited: 'Unlimited'
};

interface FeeOption {
  id: string;              // stable id: `${section}:${name || feeType}` — matches the old tab's value format.
  section: 'A' | 'B';
  label: string;           // "Section A · Appraisal Fee"
  finalAmount: number;
  tolerance: 'zero' | 'ten';
}

/**
 * Build Section A/B fee options from `loan.le_fees`.
 *
 * `le_fees` rows carry an explicit `section` ('A' | 'B' | 'C' | 'E' | 'F' |
 * 'G' | 'H'), a human-readable `name`, and a `borrowerAmount` field — see
 * `src/components/loan-estimate/useLeFieldsMapper.ts:24` for the canonical
 * shape and `src/components/banker-recon/BankerRecon.tsx:730` for a reference
 * consumer. The ProspectLoan type declares these under an index signature
 * so they're readable but not explicitly typed.
 *
 * Fallbacks (`feeType` / `feeDescription` / `borPaidAmount`) cover older
 * rows that predate the current shape.
 */
function buildFeeOptions(loan: ProspectLoan): FeeOption[] {
  if (!loan.le_fees || !Array.isArray(loan.le_fees)) {
    return [];
  }
  const out: FeeOption[] = [];
  for (const fee of loan.le_fees) {
    const section = fee.section as string | undefined;
    if (section !== 'A' && section !== 'B') {
      continue;
    }
    const name = (fee.name as string | undefined) || fee.feeDescription || fee.description || fee.feeType || 'Unknown';
    const amount = Number(
      (fee.borrowerAmount as number | undefined) ?? fee.borPaidAmount ?? 0
    ) || 0;
    out.push({
      id: `${section}:${name}`,
      section,
      label: `Section ${section} · ${name}`,
      finalAmount: amount,
      tolerance: TOLERANCE_BY_SECTION[section] as 'zero' | 'ten'
    });
  }
  return out;
}

export function ConcessionsCureModal({
  open,
  onOpenChange,
  loan,
  concessions,
  basePriceLocked,
  loanAmount,
  onLoanUpdate,
  refreshConcessions,
  netLoLocked = null,
  deleteBlockedReason = null
}: Props) {
  const [tab, setTab] = useState<Tab>('concessions');
  const sync = useEncompassFieldSync(loan.id, loan.encompass_loan_id);

  const activeCount = concessions.filter((c) => !c.is_legacy).length;
  const hasCure = (toNumberOrNull(loan.cure_applied_lender_credit) ?? 0) > 0;
  const cureCount = hasCure ? 1 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-pricing-v2
        className="ccm-root p-0 gap-0 overflow-hidden sm:max-w-[860px] w-[95vw] max-h-[calc(100vh-60px)] flex flex-col"
      >
        <DialogTitle className="sr-only">Manage Concessions and Cost to Cure</DialogTitle>
        <DialogDescription className="sr-only">
          Review concessions and apply cost-to-cure credits for this loan.
        </DialogDescription>
        <Header
          activeTab={tab}
          onTabChange={setTab}
          concessionCount={activeCount}
          cureCount={cureCount}
        />
        <div className="ccm-body">
          {tab === 'concessions' ? (
            <ConcessionsTab
              loan={loan}
              concessions={concessions}
              basePriceLocked={basePriceLocked}
              loanAmount={loanAmount}
              refreshConcessions={refreshConcessions}
              netLoLocked={netLoLocked}
              deleteBlockedReason={deleteBlockedReason}
              onClose={() => onOpenChange(false)}
            />
          ) : (
            <CureTab
              loan={loan}
              sync={sync}
              onLoanUpdate={onLoanUpdate}
              onClose={() => onOpenChange(false)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header({
  activeTab,
  onTabChange,
  concessionCount,
  cureCount
}: {
  activeTab: Tab;
  onTabChange: (t: Tab) => void;
  concessionCount: number;
  cureCount: number;
}) {
  return (
    <div className="ccm-hdr">
      <div className="ccm-hdr-row">
        <h2 className="ccm-title">Manage Concessions &amp; Cure</h2>
      </div>
      <div className="ccm-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'concessions'}
          className={`ccm-tab${activeTab === 'concessions' ? ' active' : ''}`}
          onClick={() => onTabChange('concessions')}
        >
          Concessions <span className="count">{concessionCount}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'cure'}
          className={`ccm-tab${activeTab === 'cure' ? ' active' : ''}`}
          onClick={() => onTabChange('cure')}
        >
          Cost to Cure <span className="count">{cureCount}</span>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Concessions tab
// ---------------------------------------------------------------------------

function ConcessionsTab({
  loan,
  concessions,
  basePriceLocked,
  loanAmount,
  refreshConcessions,
  netLoLocked,
  deleteBlockedReason,
  onClose: _onClose
}: {
  loan: ProspectLoan;
  concessions: LendwiseConcession[];
  basePriceLocked: number;
  loanAmount: number;
  refreshConcessions: () => Promise<void>;
  netLoLocked: number | null;
  deleteBlockedReason: string | null;
  onClose: () => void;
}) {
  const [pointsStr, setPointsStr] = useState<string>('0.2500');
  const [reason, setReason] = useState<string>('');
  const [allocMode, setAllocMode] = useState<'auto' | 'manual'>('auto');
  const [manualBucket, setManualBucket] = useState<BucketState['bucket']>('level1');
  const [isSaving, setIsSaving] = useState(false);
  const [marginGuardStep, setMarginGuardStep] = useState<0 | 1 | 2 | 3>(0);

  const { user } = useAuth();
  const isMarginOverrideUser =
    (user?.email ?? '').toLowerCase() === 'anthony@lendwisemtg.com';

  const adjustments = loan.rate_lock_buy_side_adjustments;
  const active = concessions.filter((c) => !c.is_legacy);

  const capacity = useMemo(
    () => computeWaterfallCapacity(adjustments, active),
    [adjustments, active]
  );

  const pointsNum = Number(pointsStr) || 0;
  const dollarsFromPts = Math.round(pointsNum * loanAmount * 0.01);

  // Preview — drives the "taking" overlay on bucket bars.
  const preview = useMemo(() => {
    if (pointsNum <= 0) {
return null;
}
    if (allocMode === 'auto') {
      return previewWaterfall(pointsNum, adjustments, active);
    }
    // Manual: single-bucket draw
    const bucketRemaining = capacity.buckets.find((b) => b.bucket === manualBucket)?.remaining ?? 0;
    const exceedsCapacity = pointsNum > bucketRemaining;
    const allocation: WaterfallAllocation = {
      level1_absorbed: manualBucket === 'level1' ? pointsNum : 0,
      branch_management_absorbed: manualBucket === 'branch_management' ? pointsNum : 0,
      margin_absorbed: manualBucket === 'margin' ? pointsNum : 0
    };
    return {
      bucketsBefore: capacity.buckets,
      bucketsAfter: capacity.buckets.map((b) => {
        if (b.bucket === manualBucket) {
          return {
            ...b,
            consumed: b.consumed + pointsNum,
            remaining: Math.max(0, b.remaining - pointsNum)
          };
        }
        return b;
      }),
      allocation: exceedsCapacity ? null : allocation,
      exceedsCapacity
    };
  }, [pointsNum, allocMode, manualBucket, adjustments, active, capacity.buckets]);

  const availAfter = useMemo(() => {
    if (!preview || !preview.allocation) {
return capacity.totalAvailable;
}
    return Math.max(0, capacity.totalAvailable - pointsNum);
  }, [preview, capacity.totalAvailable, pointsNum]);

  const overCapacity = preview?.exceedsCapacity ?? false;
  const reasonTrimmed = reason.trim();
  const saveDisabled =
    pointsNum <= 0
    || reasonTrimmed === ''
    || overCapacity
    || isSaving;

  const handleSave = useCallback(async () => {
    if (saveDisabled) {
return;
}
    const alloc = allocMode === 'auto'
      ? allocateWaterfall(pointsNum, adjustments, active)
      : preview!.allocation!;

    if (alloc.margin_absorbed > 0 && !isMarginOverrideUser) {
      setMarginGuardStep(1);
      return;
    }

    const totalLoPriceBefore = basePriceLocked;
    const totalLoPriceAfter = basePriceLocked + pointsNum;
    setIsSaving(true);
    try {
      await addConcession({
        loanId: loan.id,
        amount: dollarsFromPts,
        amountPoints: pointsNum,
        reason: reasonTrimmed,
        loanAmountSnapshot: loanAmount,
        totalLoPriceBefore,
        totalLoPriceAfter,
        level1Absorbed: alloc.level1_absorbed,
        branchManagementAbsorbed: alloc.branch_management_absorbed,
        marginAbsorbed: alloc.margin_absorbed
      });
      await refreshConcessions();
      toast.success('Concession added');
      // Reset form for the next add; keep modal open.
      setPointsStr('0.2500');
      setReason('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add concession');
    } finally {
      setIsSaving(false);
    }
  }, [
    saveDisabled,
    allocMode,
    pointsNum,
    adjustments,
    active,
    preview,
    basePriceLocked,
    loan.id,
    dollarsFromPts,
    reasonTrimmed,
    loanAmount,
    refreshConcessions,
    isMarginOverrideUser
  ]);

  // Same 3-decimal rule as the Set Active guard: a concession that is funding
  // par cannot be removed from the browser (the delete is a direct table
  // write with no server par check).
  const removalBelowPar = useCallback(
    (c: LendwiseConcession): boolean =>
      netLoLocked !== null
      && Number((netLoLocked - (Number(c.amount_points) || 0)).toFixed(3)) < 100,
    [netLoLocked]
  );
  // One reason per row: the candidate-lock block first, then the committed par rule.
  const removalBlockedReason = useCallback(
    (c: LendwiseConcession): string | null =>
      deleteBlockedReason
      ?? (removalBelowPar(c) ? 'Removing this concession puts the loan below par' : null),
    [deleteBlockedReason, removalBelowPar]
  );

  // One removal at a time: the next one is judged on the refreshed balance.
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = useCallback(
    async (id: string) => {
      if (isDeleting) {
        return;
      }
      const target = active.find((c) => c.id === id);
      const blocked = target ? removalBlockedReason(target) : null;
      if (blocked) {
        toast.error(blocked);
        return;
      }
      setIsDeleting(true);
      try {
        // Par guard on: the server projects par on the active lock, the live
        // Encompass pricing and the remaining concessions before deleting; the
        // inline note above is only a preview of that answer.
        if (netLoLocked !== null) {
          await deleteConcessionGuarded({ loanId: loan.id, encompassLoanId: loan.encompass_loan_id, concessionId: id });
        } else {
          await deleteConcession(id, loan.id);
        }
        await refreshConcessions();
        toast.success('Concession removed');
      } catch (err) {
        if (err instanceof ConcessionDeleteError) {
          toast.error(err.message);
          if (err.code === 'PRICING_CHANGED' || err.code === 'CONCESSION_NOT_FOUND') {
            // The server rolled the delete back because the balance or the
            // active lock moved under this request: show the current list.
            try {
              await refreshConcessions();
            } catch {
              // The list stays as it was; the toast already said to try again.
            }
          }
        } else {
          toast.error(err instanceof Error ? err.message : 'Failed to delete concession');
        }
      } finally {
        setIsDeleting(false);
      }
    },
    [active, isDeleting, loan.encompass_loan_id, loan.id, netLoLocked, refreshConcessions, removalBlockedReason]
  );

  // Determine "next draw" bucket for the Auto / Manual highlight.
  const nextDrawBucket = useMemo<BucketState['bucket'] | null>(() => {
    if (allocMode === 'manual') {
return manualBucket;
}
    const firstWithCapacity = capacity.buckets.find((b) => b.remaining > 0.00005);
    return firstWithCapacity?.bucket ?? null;
  }, [allocMode, manualBucket, capacity.buckets]);

  if (marginGuardStep !== 0) {
    return (
      <>
        <div className="ccm-guard">
          {marginGuardStep === 1 && (
            <>
              <p className="ccm-guard-title">Are you sure you want to eat into Lendwise's margins?</p>
              <p className="ccm-guard-sub">This concession would absorb from the Lendwise Margin bucket.</p>
            </>
          )}
          {marginGuardStep === 2 && (
            <p className="ccm-guard-title">Anthony will receive an email, and your job maybe in jeopardy, are you sure you want to continue?</p>
          )}
          {marginGuardStep === 3 && (
            <p className="ccm-guard-title">Ghoo khordee, you can't eat into Lendwise Margin, go away.</p>
          )}
        </div>
        <GuardFooter
          step={marginGuardStep}
          onCancel={() => setMarginGuardStep(0)}
          onAdvance={() => setMarginGuardStep((s) => (s === 1 ? 2 : 3))}
        />
      </>
    );
  }

  return (
    <>
      <div className="ccm-layout">
        {/* Form column */}
        <div className="ccm-form">
          <div className="ccm-field">
            <label>Amount</label>
            <div className="ccm-2col">
              <div className="ccm-input-wrap">
                <input
                  type="number"
                  step="0.0001"
                  value={pointsStr}
                  onChange={(e) => setPointsStr(e.target.value)}
                  aria-invalid={overCapacity}
                />
                <span className="suffix">pts</span>
              </div>
              <div className="ccm-input-wrap has-prefix">
                <span className="prefix">$</span>
                <input type="text" value={dollarsFromPts.toLocaleString()} readOnly />
              </div>
            </div>
            {overCapacity && (
              <div className="ccm-field-note" role="alert">
                Exceeds available waterfall capacity ({formatNumber(capacity.totalAvailable, 4)} pts)
              </div>
            )}
          </div>

          <div className="ccm-field">
            <label>Reason</label>
            <textarea
              placeholder="Describe why this concession is being added…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="ccm-field">
            <label>Allocation</label>
            <div className="ccm-seg">
              <button
                type="button"
                className={allocMode === 'auto' ? 'active' : ''}
                onClick={() => setAllocMode('auto')}
              >
                Auto · waterfall
              </button>
              <button
                type="button"
                className={allocMode === 'manual' ? 'active' : ''}
                onClick={() => setAllocMode('manual')}
              >
                Manual
              </button>
            </div>
          </div>

          {allocMode === 'manual' && (
            <div className="ccm-field">
              <label>Bucket</label>
              <select
                value={manualBucket}
                onChange={(e) => setManualBucket(e.target.value as BucketState['bucket'])}
              >
                {capacity.buckets.map((b) => (
                  <option key={b.bucket} value={b.bucket}>
                    {BUCKET_LABELS[b.bucket]} ({formatNumber(b.remaining, 3)} remaining)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Viz column */}
        <div className="ccm-viz">
          <div className="ccm-viz-hdr">
            <h4>Waterfall buckets</h4>
            <div className="ccm-viz-total">
              <span className="mono">{formatNumber(capacity.totalAvailable, 3)}</span>
              <span style={{ fontSize: 11, fontWeight: 500, marginLeft: 4 }}>pts available</span>
            </div>
          </div>
          <div className="ccm-buckets">
            {capacity.buckets.map((b) => {
              const after = preview?.bucketsAfter.find((x) => x.bucket === b.bucket);
              const usedPct = b.original > 0 ? (b.consumed / b.original) * 100 : 0;
              // "taking" overlay shows what the current input would consume,
              // starting right after the existing `used` portion.
              const takingStart = usedPct;
              const takingDelta = after && after.consumed > b.consumed
                ? ((after.consumed - b.consumed) / Math.max(b.original, 0.0001)) * 100
                : 0;
              const takingWidth = Math.min(100 - takingStart, Math.max(0, takingDelta));
              const isActive = nextDrawBucket === b.bucket && pointsNum > 0;
              return (
                <div
                  key={b.bucket}
                  className={`ccm-bucket${isActive ? ' waterfall-active' : ''}`}
                >
                  <div className="b-row">
                    <div className="b-name">
                      {BUCKET_LABELS[b.bucket]}
                      {isActive && <span className="chip">next draw</span>}
                    </div>
                    <div className="b-stats">
                      <span>Orig <span className="mono">{formatNumber(b.original, 3)}</span></span>
                      <span>Used <span className="mono">{formatNumber(b.consumed, 3)}</span></span>
                      <span className="rem">Rem <span className="mono">{formatNumber(b.remaining, 3)}</span></span>
                    </div>
                  </div>
                  {b.original > 0 && (
                    <div className="b-bar" aria-hidden>
                      <div className="b-used" style={{ width: `${Math.min(100, usedPct)}%` }} />
                      {takingWidth > 0 && (
                        <div
                          className="b-taking"
                          style={{ left: `${takingStart}%`, width: `${takingWidth}%` }}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="ccm-avail">
            <span className="k">Available after this concession</span>
            <span className="v">{formatNumber(availAfter, 3)}</span>
          </div>
        </div>
      </div>

      <div className="ccm-active">
        <h4>Active concessions</h4>
        {active.length === 0 ? (
          <div className="ccm-empty">
            No active concessions yet. Adding one will draw from the waterfall above.
          </div>
        ) : (
          active.map((c) => (
            <div key={c.id} className="ccm-active-item">
              <div className="pts">
                +{formatNumber(c.amount_points, 4)} pts
                <span className="src" style={{ marginLeft: 8 }}>
                  {formatCurrency(c.amount)}
                </span>
              </div>
              <div className="reason">{c.reason}</div>
              <div className="src">
                {c.creator_name} · {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
              </div>
              {removalBlockedReason(c) && (
                <div className="src">{removalBlockedReason(c)}</div>
              )}
              <button
                type="button"
                className="del"
                aria-label={`Delete concession: ${c.reason}`}
                aria-disabled={removalBlockedReason(c) || isDeleting ? true : undefined}
                title={removalBlockedReason(c) ?? undefined}
                onClick={() => {
 void handleDelete(c.id);
}}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>

      <Footer
        saveLabel="Add concession"
        saveDisabled={saveDisabled}
        isSaving={isSaving}
        onSave={() => {
 void handleSave(); 
}}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Cure tab
// ---------------------------------------------------------------------------

function CureTab({
  loan,
  sync,
  onLoanUpdate,
  onClose
}: {
  loan: ProspectLoan;
  sync: ReturnType<typeof useEncompassFieldSync>;
  onLoanUpdate?: (updates: Partial<ProspectLoan>) => void;
  onClose: () => void;
}) {
  const feeOptions = useMemo(() => buildFeeOptions(loan), [loan]);
  const isFeeLocked = Boolean(loan.cure_fee_selection);
  const currentCure = toNumberOrNull(loan.cure_applied_lender_credit) ?? 0;

  const [selectedFee, setSelectedFee] = useState<string>(loan.cure_fee_selection ?? '');
  const [amountStr, setAmountStr] = useState<string>(
    currentCure > 0 ? String(currentCure) : ''
  );
  const [date, setDate] = useState<string>(loan.tolerance_resolution_date ?? '');
  const [comments, setComments] = useState<string>(loan.tolerance_resolution_comments ?? '');
  const [isSaving, setIsSaving] = useState(false);

  // Re-seed when the loan prop changes (post-save optimistic update).
  useEffect(() => {
    setSelectedFee(loan.cure_fee_selection ?? '');
    setDate(loan.tolerance_resolution_date ?? '');
    setComments(loan.tolerance_resolution_comments ?? '');
    const next = toNumberOrNull(loan.cure_applied_lender_credit) ?? 0;
    setAmountStr(next > 0 ? String(next) : '');
  }, [
    loan.cure_fee_selection,
    loan.tolerance_resolution_date,
    loan.tolerance_resolution_comments,
    loan.cure_applied_lender_credit
  ]);

  const activeFee = useMemo(
    () => feeOptions.find((f) => f.id === (selectedFee || loan.cure_fee_selection)) ?? null,
    [feeOptions, selectedFee, loan.cure_fee_selection]
  );

  const amountNum = Number(amountStr);
  const amountInvalid = !amountStr || Number.isNaN(amountNum) || amountNum <= 0;
  const reducesCure = !amountInvalid && currentCure > 0 && amountNum < currentCure;
  const dateCleared = Boolean(loan.tolerance_resolution_date) && date.trim() === '';
  const commentsCleared = Boolean(loan.tolerance_resolution_comments) && comments.trim() === '';
  const needsFee = !isFeeLocked && !selectedFee;
  const saveDisabled =
    isSaving || amountInvalid || reducesCure || dateCleared || commentsCleared || needsFee;

  // Dirty detection — drives the optimistic update object so we only write
  // the fields that actually changed.
  const amountDirty = !amountInvalid && amountNum !== currentCure;
  const dateDirty = date !== (loan.tolerance_resolution_date ?? '');
  const commentsDirty = comments !== (loan.tolerance_resolution_comments ?? '');
  const feeDirty = !isFeeLocked && selectedFee !== '';

  const handleSave = useCallback(async () => {
    if (saveDisabled) {
return;
}
    setIsSaving(true);
    try {
      // Step 1: fee selection RPC (first write only).
      if (feeDirty) {
        const { error } = await supabase.rpc('update_cure_fee_selection', {
          p_loan_id: loan.id,
          p_fee_value: selectedFee
        });
        if (error) {
          toast.error(error.message || 'Failed to save fee selection');
          return;
        }
      }

      // Step 2: Encompass field writes, FV.X396 first so the server's
      // reduce-guard short-circuits before date/comments if the client guard
      // is somehow bypassed.
      if (amountDirty) {
        const r = await sync.enqueueWrite({
          fieldId: 'FV.X396',
          value: String(amountNum)
        });
        if (r.status === 'failed') {
          toast.error(r.error || 'Failed to save cure amount');
          return;
        }
      }
      if (dateDirty) {
        const r = await sync.enqueueWrite({ fieldId: '3171', value: date });
        if (r.status === 'failed') {
          toast.error(r.error || 'Failed to save resolution date');
          return;
        }
      }
      if (commentsDirty) {
        const r = await sync.enqueueWrite({ fieldId: '3172', value: comments });
        if (r.status === 'failed') {
          toast.error(r.error || 'Failed to save resolution comments');
          return;
        }
      }

      // Build the optimistic update object — only dirty keys. Types declare
      // these as optional strings, not string|null, so send values only.
      const updates: Partial<ProspectLoan> = {};
      if (amountDirty) {
updates.cure_applied_lender_credit = amountNum;
}
      if (dateDirty) {
updates.tolerance_resolution_date = date;
}
      if (commentsDirty) {
updates.tolerance_resolution_comments = comments;
}
      if (feeDirty) {
updates.cure_fee_selection = selectedFee;
}
      if (Object.keys(updates).length > 0) {
onLoanUpdate?.(updates);
}

      toast.success('Cure saved');
      onClose();
    } finally {
      setIsSaving(false);
    }
  }, [
    saveDisabled,
    feeDirty,
    amountDirty,
    dateDirty,
    commentsDirty,
    selectedFee,
    amountNum,
    date,
    comments,
    loan.id,
    sync,
    onLoanUpdate,
    onClose
  ]);

  return (
    <>
      <div className="ccm-layout">
        {/* Form column */}
        <div className="ccm-form">
          <div className="ccm-field">
            <label>Fee selection</label>
            <select
              value={selectedFee}
              onChange={(e) => setSelectedFee(e.target.value)}
              disabled={isFeeLocked}
            >
              <option value="">Select a fee…</option>
              {feeOptions.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
            {activeFee && (
              <div className="ccm-fee-ctx">
                <div className="fc-hdr">
                  <span className="fc-name">{activeFee.label}</span>
                  <span className="fc-tol">{TOLERANCE_LABEL[activeFee.tolerance]}</span>
                </div>
                <div className="fc-nums">
                  <div>
                    <div className="k">Final</div>
                    <div className="v mono">{formatCurrency(activeFee.finalAmount)}</div>
                  </div>
                  <div>
                    <div className="k">Current cure</div>
                    <div className="v mono">{formatCurrency(currentCure)}</div>
                  </div>
                  <div>
                    <div className="k">New cure</div>
                    <div className={`v mono ${amountNum > currentCure ? 'pos' : ''}`}>
                      {amountInvalid ? '—' : formatCurrency(amountNum)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="ccm-field">
            <label>Cure amount</label>
            <div className="ccm-input-wrap has-prefix">
              <span className="prefix">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                aria-invalid={amountInvalid || reducesCure}
              />
            </div>
            {reducesCure && (
              <div className="ccm-field-note" role="alert">
                Cannot reduce cure amount (current: {formatCurrency(currentCure)}).
              </div>
            )}
          </div>

          <div className="ccm-field">
            <label>Resolution date</label>
            <div className="ccm-2col">
              <div className="ccm-input-wrap">
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  aria-invalid={dateCleared}
                />
              </div>
              <div />
            </div>
            {dateCleared && (
              <div className="ccm-field-note" role="alert">
                Resolution date cannot be cleared once set.
              </div>
            )}
          </div>

          <div className="ccm-field">
            <label>Comments</label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Describe the cure reason…"
              aria-invalid={commentsCleared}
            />
            {commentsCleared && (
              <div className="ccm-field-note" role="alert">
                Resolution comments cannot be cleared once set.
              </div>
            )}
          </div>

          <div className="ccm-warn-inline">
            <svg className="i" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <path d="M12 9v4M12 17h.01" />
            </svg>
            <div>
              Cures are immutable. Once saved, the cure amount cannot be reduced or cleared — only additional cures can be added.
            </div>
          </div>
        </div>

        {/* Viz column */}
        <div className="ccm-viz">
          <div className="ccm-viz-hdr">
            <h4>Cure applied</h4>
            <div className="ccm-viz-total" style={{ color: currentCure > 0 ? undefined : 'var(--ink-3)' }}>
              {formatCurrency(currentCure)}
            </div>
          </div>
          {currentCure > 0 ? (
            <div className="ccm-cure-summary">
              <div className="amount">{formatCurrency(currentCure)}</div>
              <div className="meta">
                {loan.cure_fee_selection ?? 'No fee tied'}
                {loan.tolerance_resolution_date ? ` · ${loan.tolerance_resolution_date}` : ''}
              </div>
              {loan.tolerance_resolution_comments && (
                <div className="meta">{loan.tolerance_resolution_comments}</div>
              )}
            </div>
          ) : (
            <div className="ccm-empty">
              No cure applied yet. Pick a fee and enter an amount to record one.
            </div>
          )}
          <div className="ccm-tolerance-reminder">
            <div className="title">Tolerance reminder</div>
            <div className="row"><b>0% tolerance</b><span>Section A lender fees</span></div>
            <div className="row"><b>10% tolerance</b><span>Section B permissible</span></div>
            <div className="row"><b>Unlimited</b><span>Section C borrower choice</span></div>
          </div>
        </div>
      </div>

      <Footer
        saveLabel="Save cure"
        saveDisabled={saveDisabled}
        isSaving={isSaving}
        onSave={() => {
 void handleSave(); 
}}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function GuardFooter({
  step,
  onCancel,
  onAdvance
}: {
  step: 1 | 2 | 3;
  onCancel: () => void;
  onAdvance: () => void;
}) {
  return (
    <div className="ccm-footer">
      <div className="hint" />
      <div className="actions">
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        {step < 3 && (
          <button type="button" className="btn accent" onClick={onAdvance}>Yes</button>
        )}
      </div>
    </div>
  );
}

function Footer({
  saveLabel,
  saveDisabled,
  isSaving,
  onSave
}: {
  saveLabel: string;
  saveDisabled: boolean;
  isSaving: boolean;
  onSave: () => void;
}) {
  // Ctrl/Cmd+Enter triggers save on the active tab.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!saveDisabled) {
          onSave();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [saveDisabled, onSave]);

  return (
    <div className="ccm-footer">
      <div className="hint">
        <span className="kbd">Esc</span> cancel ·{' '}
        <span className="kbd">Ctrl/⌘</span>
        <span className="kbd">↵</span> save
      </div>
      <div className="actions">
        <button
          type="button"
          className="btn accent"
          disabled={saveDisabled}
          onClick={onSave}
        >
          {isSaving ? 'Saving…' : saveLabel}
        </button>
      </div>
    </div>
  );
}
